import Image from 'next/image';

import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';
import { Text } from '~/domains/design-system/Text';

import { useStyles, MISSION_BADGE_DIMENSIONS, MISSION_BADGE_BGR_DIMENSIONS } from './MissionBadge.styles';
import type { MissionBadgeSize } from './MissionBadge.styles';

interface MissionBadgeProps {
  /** small = card, medium = completed modal, large = started modal. */
  size: MissionBadgeSize;
  /** Value shown in the badge (generic variant); already `$`-formatted by the API. */
  rewardValue?: string;
  /** When present, the icon variant overrides `rewardValue`. */
  iconUrl?: string | null;
  alt?: string;
}

/**
 * The hexagon reward badge shared by the mission card and detail modals.
 * The background hexagon has small/medium/large CDN variants.
 */
export function MissionBadge({ size, rewardValue, iconUrl, alt = 'Challenge badge' }: MissionBadgeProps) {
  const { classes } = useStyles({ size });
  const dimension = MISSION_BADGE_DIMENSIONS[size];
  const bgrDimension = MISSION_BADGE_BGR_DIMENSIONS[size];

  return (
    <div className={classes.badge}>
      {iconUrl ? (
        <Image
          src={iconUrl}
          alt={alt}
          width={Math.round(dimension * 0.63)}
          height={Math.round(dimension * 0.63)}
          className={classes.badgeIcon}
        />
      ) : (
          <Text
            variant="display-headline-condensed-xl"
            className={classes.rewardValue}
          >
          {rewardValue}
        </Text>
      )}
      <Image
        className={classes.badgeBgr}
        src={getCdnUrl(`/images/loyalty/MissionBadge-${size}.png`)}
        alt={alt}
        width={bgrDimension[0]}
        height={bgrDimension[1]}
      />

      <svg width="0" height="0" aria-hidden="true">
        <filter id="rewardValueShadow">
          <feOffset dx="1" dy="1" />
          <feGaussianBlur stdDeviation="0.75" />
          <feComposite operator="out" in="SourceAlpha" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" />
          <feBlend in2="SourceGraphic" />
        </filter>
      </svg>
    </div>
  );
}
